<script lang="ts" setup>
import { onMounted } from "vue";
import Button from "@/shared/components/Button/Button.vue";
import Captcha from "@/shared/components/Captcha/Captcha.vue";
import Form from "@/shared/components/Form/Form.vue";
import SelectField from "@/shared/components/SelectField/SelectField.vue";
import Terms from "@/shared/components/Terms/Terms.vue";
import TextField from "@/shared/components/TextField/TextField.vue";
import type { InitialRegistrationData } from "@/app/registration-app/user/shared/@types/InitialRegistrationData";
import type { PersonalRegistrationData } from "@/app/registration-app/user/shared/@types/PersonalRegistrationData";
import { usePersonalInformationFields } from "./usePersonalInformationFields.composable";

const props = defineProps<{
  initialData?: InitialRegistrationData;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: "validate-data", fields: PersonalRegistrationData): void;
}>();

const {
  salutationOptions,
  salutation: { value: salutation, errors: salutationErrors },
  firstName: { value: firstName, errors: firstNameErrors },
  lastName: { value: lastName, errors: lastNameErrors },
  phoneNumber: { value: phoneNumber, errors: phoneNumberErrors },
  captcha: { value: captcha },
  handleSubmit,
  resetForm,
} = usePersonalInformationFields(props.initialData);

const onSubmit = handleSubmit(() => {
  const data = {
    firstName: firstName.value,
    lastName: lastName.value,
    salutation: salutation.value,
    phoneNumber: phoneNumber.value,
    captcha: captcha.value,
  };
  emit("validate-data", data);
});

onMounted(() => resetForm());
</script>

<template>
  <Form
    novalidate
    @submit="onSubmit"
  >
    <template #default>
      <SelectField
        v-model="salutation"
        :options="salutationOptions"
        :errors="salutationErrors"
        :label="$t('shared.address.salutation')"
      />
      <TextField
        v-model="firstName"
        :errors="firstNameErrors"
        :label="$t('shared.address.firstName')"
        :placeholder="$t('shared.address.firstName')"
        required
      />
      <TextField
        v-model="lastName"
        :errors="lastNameErrors"
        :label="$t('shared.address.lastName')"
        :placeholder="$t('shared.address.lastName')"
        required
      />
      <TextField
        v-model="phoneNumber"
        :label="$t('shared.address.phoneNumber')"
        :placeholder="$t('shared.address.phoneNumber')"
        :errors="phoneNumberErrors"
        :hint="$t('shared.form.field.phoneNumber.hint')"
      />
      <Terms />
      <Captcha
        v-model="captcha"
        class="personal-information-form--captcha"
      />
    </template>
    <template #actions>
      <Button
        type="submit"
        :caption="$t('registration.shared.action.register')"
        :is-loading="isLoading"
      />
    </template>
  </Form>
</template>

<style scoped>
@import "PersonalInformationForm.css";
</style>
